import * as React from "react";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FormikHelpers } from "formik";
import { isNil } from "ramda";

import { AffinityForm, FormView } from "affinityForm";
import { Layout } from "affinityUI/structures/layout";
import { useCreateExhPatient } from "components/pages/patients/patients/hooks/useCreateExhPatient";
import { patientErrorFieldConverter } from "components/pages/patients/patients/utils/patientConverters";
import {
  PatientConfirmation,
  PatientForm,
  PatientFormData,
  PatientsFormContext,
} from "components/views/patientDetails/patientEdit";
import { patientCreateValidationSchema } from "components/views/patientDetails/patientEdit/patientForm/validationSchema";
import { useAuth } from "lib/auth";

export const CreatePatientPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth()[1];
  const { createPatient: createExhPatient, loading: isSubmitting } = useCreateExhPatient();

  const createPatient = useCallback(
    async (formData: PatientFormData, formikHelpers?: FormikHelpers<PatientFormData>) => {
      if (isNil(currentUser) || isNil(currentUser?.hospital) || isNil(currentUser?.hospital?.id)) {
        return;
      }

      const result = await createExhPatient(formData);

      if (!result) {
        return;
      }

      if (result.status === "validation_error") {
        Object.entries(result.errors).forEach(([key, message]) => {
          formikHelpers?.setFieldError(patientErrorFieldConverter(key) ?? key, message);
        });
        navigate("/patients/add");
        return;
      }

      navigate(`/patients/${result.data.id}`, { replace: true });
    },
    [currentUser, createExhPatient, navigate]
  );

  return (
    <AffinityForm<PatientFormData>
      validationSchema={patientCreateValidationSchema}
      initialData={{ contactDetails: { address: { countryCode: "US" } } } as PatientFormData}
      context={PatientsFormContext}
      confirmHandler={createPatient}
    >
      <FormView
        form={
          <Layout title="Add patient" showBackBtn>
            <PatientForm />
          </Layout>
        }
        confirm={
          <Layout
            title="Confirm patient"
            showBackBtn
            backLink=".."
            backLinkText="Back to new patient form"
            loading={isSubmitting}
          >
            <PatientConfirmation context={PatientsFormContext} />
          </Layout>
        }
      />
    </AffinityForm>
  );
};
